export type CardKind = 'direct_announcements' | 'requests' | 'announcement_responses'

export type CardChangedBy = 'user' | 'system' | 'admin'

export type CardTone = 'gray' | 'green' | 'red' | 'yellow' | 'dark'

export type CardRole = 'parent' | 'sitter' | 'visitor'

export type CardTitleKey =
  | 'pending'
  | 'accepted'
  | 'accepted_admin'
  | 'started'
  | 'fulfilled'
  | 'declined_user'
  | 'declined_system'
  | 'declined_admin'
  | 'rejected'
  | 'canceled_user'
  | 'canceled_other'
  | 'missed'
  | 'unknown'

export type CardStatusSource = {
  status: string
  closed_reason?: string | null
  status_changed_by?: string | null
}

export type CardModel = {
  tone: CardTone
  changedBy: CardChangedBy
  titleKey: CardTitleKey
  reasonLabel: 'default' | 'sitter_visible'
  canFindAnother: boolean
}

const isChangedBy = (value: unknown): value is CardChangedBy => value === 'user' || value === 'system' || value === 'admin'

/** Until the backend sends `status_changed_by`, the author is derived from statuses only the system produces; a request fulfilled by a person shows none. */
export const statusChangedBy = (kind: CardKind, source: CardStatusSource): CardChangedBy => {
  if (isChangedBy(source.status_changed_by)) return source.status_changed_by
  switch (kind) {
    case 'direct_announcements':
      return source.closed_reason === 'sitter_busy' || source.status === 'missed' ? 'system' : 'user'
    case 'requests':
      return source.status === 'missed' ? 'system' : 'user'
    case 'announcement_responses':
      return source.status === 'passed' ? 'system' : 'user'
    default:
      return 'user'
  }
}

const negativeTone = (changedBy: CardChangedBy, adminTone: CardTone): CardTone => (changedBy === 'system' ? 'yellow' : adminTone)

const toneOf = (status: string, changedBy: CardChangedBy): CardTone => {
  switch (status) {
    case 'accepted':
    case 'created_accepted':
    case 'started':
    case 'fulfilled':
      return 'green'
    case 'declined':
    case 'rejected':
      return negativeTone(changedBy, 'red')
    case 'canceled':
      return negativeTone(changedBy, 'dark')
    case 'missed':
    case 'passed':
      return 'yellow'
    default:
      return 'gray'
  }
}

const titleKeyOf = (status: string, changedBy: CardChangedBy): CardTitleKey => {
  switch (status) {
    case 'pending':
    case 'created':
    case 'active':
      return 'pending'
    case 'accepted':
    case 'created_accepted':
      return changedBy === 'admin' ? 'accepted_admin' : 'accepted'
    case 'started':
      return 'started'
    case 'fulfilled':
      return 'fulfilled'
    case 'declined':
      return `declined_${changedBy}`
    case 'rejected':
      return changedBy === 'admin' ? 'declined_admin' : 'rejected'
    case 'canceled':
      return changedBy === 'user' ? 'canceled_user' : 'canceled_other'
    case 'missed':
    case 'passed':
      return 'missed'
    default:
      return 'unknown'
  }
}

export const describeCard = (kind: CardKind, source: CardStatusSource, role: CardRole): CardModel => {
  const changedBy = statusChangedBy(kind, source)
  const closedBySitter = source.status === 'declined' || source.status === 'rejected'
  return {
    tone: toneOf(source.status, changedBy),
    changedBy,
    titleKey: titleKeyOf(source.status, changedBy),
    reasonLabel: kind === 'direct_announcements' && source.status === 'declined' && role === 'sitter' && changedBy === 'user' ? 'sitter_visible' : 'default',
    canFindAnother: role === 'parent' && kind !== 'announcement_responses' && closedBySitter,
  }
}
